import { CohortStepStatus, CohortStepType, ConceptCodesPayload } from 'src/types/cohorts';
import { motion } from 'motion/react';
import { useDispatch, useSelector } from 'react-redux';
import { cohortStepsUpdated, selectCohortId, selectCohortState } from 'src/store/create-cohort';
import { CodesTable } from './codes-selection-components/codes-table';
import { ButtonV2, CircularLoader } from '@medeloop/medeloop-components-library';
import { useTranslation } from 'react-i18next';
import { useGetConceptCodes } from 'src/api/concept-codes/queries';

export interface CodesSelectionStepProps {
  step: {
    data: ConceptCodesPayload[];
    type: CohortStepType;
    status: CohortStepStatus;
  };
  isActive: boolean;
  isCompleted: boolean;
  onAccept: () => void;
  onEdit: (args: { step: CohortStepType; editedContent: string }) => void;
  onCreateCohort: () => void;
  isRunJobPending: boolean;
}

export const CodesSelectionStep = ({
  step,
  onCreateCohort,
  isRunJobPending,
}: CodesSelectionStepProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cohortId = useSelector(selectCohortId);
  const { data: conceptCodes, isLoading } = useGetConceptCodes({ jobId: cohortId || '' });

  const allSavedCodesets = useSelector(selectCohortState).allCodesets || [];
  const cohortState = useSelector(selectCohortState);

  const updateStepsAndCreateCohort = async () => {
    try {
      // Check if step data exists and has the required structure
      if (!step.data || step.data.length === 0) {
        const cohortStepData = cohortState.steps[CohortStepType.CodesSelection];
        if (cohortStepData?.data && cohortStepData.data.length > 0 && cohortStepData.data[0]?.payload) {
          // Use the data from cohortState instead
          await onCreateCohort();
          return;
        }
        await onCreateCohort();
        return;
      }

      if (!step.data[0]?.payload?.content) {
        // If step data is not available, just create the cohort without updating steps
        await onCreateCohort();
        return;
      }

      // Update the steps using the active tabs state in redux
      const tableIndexToSelectedTabMap = cohortState.activeTabs || {};
      
      const updatedCodesetsStep = step.data[0].payload.content.map((data, index) => {
        const selectedTab = tableIndexToSelectedTabMap[index];
        if (selectedTab === 'saved') {
          const savedCodeSetId = data.matches?.saved?.code_list_id;
          const savedCodeSet = allSavedCodesets.find((codeset) => codeset.id === savedCodeSetId);
          return {
            ...data,
            matches: {
              ...data.matches,
              recommended: {
                ...data.matches.recommended,
                data: savedCodeSet?.data || data.matches.recommended.data,
              },
            },
          };
        }

        return data;
      });

      const existingCodesSelectionStep = cohortState.steps[CohortStepType.CodesSelection] || {};

      dispatch(
        cohortStepsUpdated({
          ...cohortState.steps,
          [CohortStepType.CodesSelection]: {
            ...existingCodesSelectionStep,
            data: [
              {
                ...step.data[0],
                payload: {
                  ...step.data[0].payload,
                  content: updatedCodesetsStep,
                },
              },
            ],
          },
        }),
      );

      // Create the cohort using the updated steps
      await onCreateCohort();
    } catch (error) {
      try {
        await onCreateCohort();
      } catch (createError) {
        // Silently fail - the error will be handled by the parent component
      }
    }
  };

  return (
    <motion.div className={`mb-6`} transition={{ duration: 0.5 }}>
      {!isLoading &&
        conceptCodes?.data &&
        conceptCodes?.data.length > 0 &&
        conceptCodes?.data.map((data, contentIndex) => (
          <motion.div
            key={`codes-table-${contentIndex}-${allSavedCodesets.length}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + contentIndex * 0.1, duration: 0.4 }}
          >
            <CodesTable
              jobId={cohortId || ''}
              data={{
                ...data,
                matches: {
                  ...data.matches,
                  recommended: {
                    ...data.matches.recommended,
                    data: data.matches.recommended.data.map((item) => ({
                      ...item,
                      relevance:
                        typeof item.relevance === 'string'
                          ? parseFloat(item.relevance)
                          : item.relevance,
                    })),
                  },
                },
              }}
              tableIndex={contentIndex}
              className="mb-6"
            />
          </motion.div>
        ))}
      <motion.div
        className="mt-6 flex justify-end"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.5,
        }}
      >
        <ButtonV2 onClick={updateStepsAndCreateCohort}>
          {isRunJobPending ? <CircularLoader /> : <span>{t('create_cohort')}</span>}
        </ButtonV2>
      </motion.div>
    </motion.div>
  );
};
